import axiosServices from "./axios";

const normalizeReportsError = (error, fallbackMessage = "Failed to fetch reports.") => {
if (error.response?.status === 403 && error.response?.data?.errorType === "LIMIT_REACHED") {
const limitError = new Error(
error.response.data.message ||
"Reports are not included in your current plan. Please upgrade to access Profile and Post Reports."
);
limitError.isLimitError = true;
limitError.errorType = "LIMIT_REACHED";
limitError.feature = error.response.data.feature || "Reports";
return limitError;
}

return new Error(
error.response?.data?.message ||
error.message ||
fallbackMessage
);
};

const normalizeProfileSummaryResponse = (data = {}) => {
  const report = data?.report || null;
  if (!report) {
    return {
      ...data,
      report: null,
    };
  }

  return {
    ...data,
    report: {
      ...report,
      metrics: {
        audience: report.metrics?.audience || 0,
        audience_growth: report.metrics?.audience_growth || 0,
        impressions: report.metrics?.impressions || 0,
        engagements: report.metrics?.engagements || 0,
        engaged_audience: report.metrics?.engaged_audience || 0,
        engagement_rate_per_impression:
          report.metrics?.engagement_rate_per_impression || 0,
        posts_count: report.metrics?.posts_count || 0,
        video_views: report.metrics?.video_views || 0,
        clicks: report.metrics?.clicks || 0,
        saves: report.metrics?.saves || 0,
        reach: report.metrics?.reach || 0,
        page_views: report.metrics?.page_views || 0,
        unique_page_views: report.metrics?.unique_page_views || 0,
        monthly_views: report.metrics?.monthly_views || 0,
        },
      },
  };
};

const normalizeProfileTimeseriesResponse = (data = {}) => {
const reports = Array.isArray(data?.reports) ? data.reports : [];
return {
...data,
reports: reports.map((report) => ({
...report,
metrics: {
audience: report.metrics?.audience || 0,
audience_growth: report.metrics?.audience_growth || 0,
impressions: report.metrics?.impressions || 0,
engagements: report.metrics?.engagements || 0,
engaged_audience: report.metrics?.engaged_audience || 0,
engagement_rate_per_impression:
report.metrics?.engagement_rate_per_impression || 0,
posts_count: report.metrics?.posts_count || 0,
video_views: report.metrics?.video_views || 0,
clicks: report.metrics?.clicks || 0,
saves: report.metrics?.saves || 0,
reach: report.metrics?.reach || 0,
page_views: report.metrics?.page_views || 0,
unique_page_views: report.metrics?.unique_page_views || 0,
monthly_views: report.metrics?.monthly_views || 0,
},
})),
};
};

const normalizePostReportItem = (report = {}) => {
const rawMetricsWindow =
report.metricsWindow ||
report.raw?.pinterest_metrics_window ||
report.raw?.metrics_window ||
null;

const metricsWindowStart =
report.metricsWindowStart ||
rawMetricsWindow?.startDate ||
rawMetricsWindow?.start_date ||
rawMetricsWindow?.start ||
null;

const metricsWindowEnd =
report.metricsWindowEnd ||
rawMetricsWindow?.endDate ||
rawMetricsWindow?.end_date ||
rawMetricsWindow?.end ||
null;

const metricsWindowDays =
report.metricsWindowDays ||
rawMetricsWindow?.daysBack ||
rawMetricsWindow?.days_back ||
rawMetricsWindow?.days ||
null;

return {
...report,
metricsWindow: rawMetricsWindow
? {
...rawMetricsWindow,
startDate: metricsWindowStart,
endDate: metricsWindowEnd,
daysBack: metricsWindowDays,
}
: null,
metricsWindowStart,
metricsWindowEnd,
metricsWindowDays,
analyticsDataStatus:
report.analyticsDataStatus ||
report.raw?.data_status ||
report.raw?.analyticsDataStatus ||
null,
reaction_breakdown: report.reaction_breakdown || {},
preview: {
image_url: Array.isArray(report.preview?.image_url)
? report.preview.image_url
: [],
video_url: Array.isArray(report.preview?.video_url)
? report.preview.video_url
: [],
source_image_url: Array.isArray(report.preview?.source_image_url)
? report.preview.source_image_url
: [],
source_video_url: Array.isArray(report.preview?.source_video_url)
? report.preview.source_video_url
: [],
},
metrics: {
likes: report.metrics?.likes || 0,
total_reactions: report.metrics?.total_reactions || 0,
comments: report.metrics?.comments || 0,
shares: report.metrics?.shares || 0,
impressions: report.metrics?.impressions || 0,
reach: report.metrics?.reach || 0,
engaged_users: report.metrics?.engaged_users || 0,
clicks: report.metrics?.clicks || 0,
engagements: report.metrics?.engagements || 0,
engagement_rate_per_impression:
report.metrics?.engagement_rate_per_impression || 0,
video_views: report.metrics?.video_views || 0,
video_view_time: report.metrics?.video_view_time || 0,
},
};
};

const normalizePostReportsResponse = (data = {}) => {
const items = Array.isArray(data?.items) ? data.items : [];
return {
...data,
items: items.map(normalizePostReportItem),
pagination: data?.pagination || {
page: 1,
limit: 20,
total: 0,
pages: 0,
},
};
};

const normalizeSinglePostReportResponse = (data = {}) => {
if (!data?.report) {
return {
...data,
report: null,
};
}

return {
...data,
report: normalizePostReportItem(data.report),
};
};

export const fetchProfileReportSummary = async ({
groupId,
platform = "YOUTUBE",
profileId,
since,
until,
}) => {
  try {
    const response = await axiosServices.get("/reports/profiles/summary", {
      params: {
        groupId,
        platform,
        profileId,
        since,
        until,
      },
    });
    return normalizeProfileSummaryResponse(response.data);
  } catch (error) {
console.error("Error fetching profile report summary:", error);
throw normalizeReportsError(error, "Failed to fetch profile report summary.");
}
};

export const fetchProfileReportTimeseries = async ({
  groupId,
  platform,
  profileId,
  limit,
  since,
  until,
}) => {
  try {
    const response = await axiosServices.get("/reports/profiles/timeseries", {
      params: {
        groupId,
        platform,
        profileId,
        limit,
        since,
        until,
      },
    });
    return normalizeProfileTimeseriesResponse(response.data);
  } catch (error) {
console.error("Error fetching profile report timeseries:", error);
throw normalizeReportsError(error, "Failed to fetch profile report timeseries.");
}
};

export const fetchPostReports = async ({
groupId,
platform = "YOUTUBE",
profileId,
page = 1,
limit = 20,
since,
until,
}) => {
try {
const response = await axiosServices.get("/reports/posts", {
params: { groupId, platform, profileId, page, limit, since, until },
});
return normalizePostReportsResponse(response.data);
} catch (error) {
console.error("Error fetching post reports:", error);
throw normalizeReportsError(error, "Failed to fetch post reports.");
}
};

export const fetchSinglePostReport = async (postId, params = {}) => {
try {
const response = await axiosServices.get(`/reports/posts/${postId}`, {
params,
});
return normalizeSinglePostReportResponse(response.data);
} catch (error) {
console.error("Error fetching single post report:", error);
throw normalizeReportsError(error, "Failed to fetch post report.");
}
};